/**
 * Service for handling duplicate fee detection and display in pricing tables.
 * Detects duplicate fees (same fee type/title/coverage) and numbers them for display.
 * Provides consistent sorting and treats cross-coverage fees as separate, not duplicates.
 */
import { Injectable } from '@angular/core';
import { Policy } from '@wsphere/warranties/domain';

@Injectable({
  providedIn: 'root',
})
export class DuplicateFeeHandlerService {
  /**
   * Automatically process duplicates when fees are loaded
   * This shows TRUE duplicate fees (same fee type/title/coverage) but treats cross-coverage fees as separate
   */
  processDuplicatesAutomatically(selectedFees: Policy.PlanCoverageFee.Model[]): Policy.PlanCoverageFee.Model[] {
    if (!selectedFees?.length) return selectedFees;

    // Group fees by type + title + coverage (true duplicates are same fee for same coverage)
    const feeGroups = new Map<string, Policy.PlanCoverageFee.Model[]>();

    // Single pass grouping - more efficient than forEach with multiple Map operations
    for (const fee of selectedFees) {
      const key = `${fee.feeType}||__||${fee.title}||__||${fee.coverageId}`;
      const existingGroup = feeGroups.get(key);
      if (existingGroup) {
        existingGroup.push(fee);
      } else {
        feeGroups.set(key, [fee]);
      }
    }

    // Pre-allocate array size for better performance
    const visibleFees: Policy.PlanCoverageFee.Model[] = [];
    let totalFees = 0;

    // Calculate total size first to avoid array resizing
    for (const groupFees of feeGroups.values()) {
      totalFees += groupFees.length;
    }

    visibleFees.length = totalFees;
    let currentIndex = 0;

    // Process groups efficiently
    for (const [feeKey, groupFees] of feeGroups) {
      if (groupFees.length > 1) {
        const [, baseTitle] = feeKey.split('||__||');

        // Process duplicates with minimal object creation
        for (let i = 0; i < groupFees.length; i++) {
          const fee = groupFees[i];
          visibleFees[currentIndex] = {
            ...fee,
            title: i === 0 ? baseTitle : `${baseTitle} (${i + 1})`,
          };
          currentIndex++;
        }
      } else {
        // Single fee - direct assignment
        visibleFees[currentIndex] = groupFees[0];
        currentIndex++;
      }
    }

    return visibleFees;
  }

  /**
   * Check if there are any duplicate fees that could be causing cost discrepancies
   */
  hasDuplicateFees(selectedFees: Policy.PlanCoverageFee.Model[]): boolean {
    if (!selectedFees?.length) return false;

    const feeGroups = new Map<string, Policy.PlanCoverageFee.Model[]>();

    // Group fees by type, title, and coverage to identify duplicates
    selectedFees.forEach(fee => {
      const key = `${fee.feeType}||__||${fee.title}||__||${fee.coverageId}`;
      if (!feeGroups.has(key)) {
        feeGroups.set(key, []);
      }
      const group = feeGroups.get(key);
      if (group) {
        group.push(fee);
      }
    });

    // Check if any group has duplicates
    for (const groupFees of feeGroups.values()) {
      if (groupFees.length > 1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get unique fee types with proper sorting and duplicate handling
   * This includes numbered duplicates as separate fee types
   */
  getUniqueFeeTypesWithDuplicates(selectedFees: Policy.PlanCoverageFee.Model[]): string[] {
    if (!selectedFees?.length) return [];

    const feeTypeSet = new Set<string>();
    const feeTypes: string[] = [];

    // Process fees in a consistent order to maintain stable sorting
    const sortedFees = [...selectedFees].sort((a, b) => {
      // First sort by feeType, then by title (null-safe)
      const typeA = a.feeType || '';
      const typeB = b.feeType || '';
      const typeCompare = typeA.localeCompare(typeB);
      if (typeCompare !== 0) return typeCompare;
      const titleA = a.title || '';
      const titleB = b.title || '';
      return titleA.localeCompare(titleB);
    });

    // Get unique fee types INCLUDING numbered duplicates
    for (const fee of sortedFees) {
      // Keep the full title including numbering for true duplicates
      const fullTitle = fee.title || '';
      const feeTypeKey = `${fee.feeType ?? ''}||__||${fullTitle}`;
      if (!feeTypeSet.has(feeTypeKey)) {
        feeTypeSet.add(feeTypeKey);
        feeTypes.push(feeTypeKey);
      }
    }

    return feeTypes;
  }
}
